<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 p-1">
      <h2 class="mb-0 main-title">Work Order Categories</h2>
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-md btn-primary fs-11 fw-normal text-white hover-white" @click="openCreateModal('createModal')">
         <i class="fa fa-plus fs-11 hover-white"></i>  Add New Category
        </button>
      
      </div>
    </div>

    

    <div class="card p-1 bg-white">
      <!-- Create Modal -->
      <FormModal ref="createModal" title="Create New Category" id="create_category_form_modal">
        <CreateCategory 
          ref="CreateCategoryForm" 
          @created="onCreated" 
          @close="closeCreateModal('createModal')" 
        />
      </FormModal>

      <!-- Edit Modal -->
      <FormModal ref="editModal" title="Edit Category" id="edit_category_form_modal">
        <EditCategory 
          v-if="editingCategory"
          :key="editingCategory.id"
          ref="EditCategoryForm"
          :category="editingCategory"
          @updated="onUpdated"
          @close="closeEditModal"
        />
      </FormModal>

      <!-- Loading State -->
      <LoadingBar v-if="loading" message="Loading Categories..." />

      <!-- Categories Table -->
      <div v-else class="table-wrapper">
        <ListTable>
          <thead>
            <tr>
              <th @click="sortBy('name')" class="sortable">
                Name <i :class="getSortIcon('name')"></i>
              </th>
              <th @click="sortBy('created_at')" class="sortable">
                Created At <i :class="getSortIcon('created_at')"></i>
              </th>
              <th @click="sortBy('description')" class="sortable">
                Description <i :class="getSortIcon('description')"></i>
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="category in sortedData" :key="category.id">
              <td>
                <div class="mb-0">{{ category.name }}</div>
                <div class="mt-0">
                  <small class="text-muted">#{{ category.external_id?.slice(0, 8) || category.id }}</small>
                </div>
              </td>
              <td>
                <span class="">{{ category.created_at }}</span>
              </td>
              <td>{{ category.description || '—' }}
              </td>
              <td>
                 <TableActionDropdown>
                  <li><button class="dropdown-item" @click.prevent="viewCategory(category)"><i class="fa fa-eye text-info "></i>View Details</button></li>
                  <li><button class="dropdown-item" @click.prevent="editCategory(category)"><i class="fa fa-pencil text-primary "></i>Edit</button></li>
                  <li><button class="dropdown-item text-danger" @click.prevent="deleteCategory(category)"><i class="fa fa-trash text-danger "></i>Delete</button></li>
                </TableActionDropdown>
              </td>
            </tr>
            <tr v-if="categories.length === 0">
              <td colspan="4" class="text-center py-4">
                <i class="fa fa-folder-open fa-2x text-muted mb-2 d-block"></i>
                No Categories Found
              </td>
            </tr>
          </tbody>
        </ListTable>
      </div>

      <!-- Pagination -->
      <Pagination :pagination="pagination" @page-change="changePage" />
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import CreateCategory from '@/views/WO/Category/CreateWOCategory.vue'
import EditCategory from '@/views/WO/Category/EditWOCategory.vue'
import ListTable from '@/components/utils/ListTable.vue'
import TableActionDropdown from '@/components/TableActionDropdown.vue'

export default {
  name: 'CategoryList',
  components: { 
    Pagination, 
    LoadingBar, 
    FormModal, 
    CreateCategory, 
    EditCategory,
    ListTable,
    TableActionDropdown 
  },
  data() {
    return {
      categories: [],
      pagination: {
        total_rows: 0,
        per_page: 20,
        current_page: 1,
        total_pages: 1
      },
      loading: false,
      filterManager: null,
      sortColumn: 'name',
      sortOrder: 'asc',
      currentFilters: {},
      editingCategory: null,
    }
  },
  computed: {
    sortedData() {
      let result = [...this.categories];
      
      if (this.sortColumn) {
        result.sort((a, b) => {
          let valA = a[this.sortColumn];
          let valB = b[this.sortColumn];
          
          if (valA === undefined || valA === null) valA = '';
          if (valB === undefined || valB === null) valB = '';
          
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
          
          if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
          if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }
      
      return result;
    }
  },
  mounted() {
    this.fetchList()
    this.$nextTick(() => {
      setTimeout(() => {
        this.initFilters();
      }, 100);
    });
  },
  methods: {
    openCreateModal(ref) {
      if (this.$refs[ref]) {
        this.$refs[ref].showModal()
      }
      if (this.$refs.CreateCategoryForm) {
        this.$refs.CreateCategoryForm.resetForm()
      }
    },

    closeCreateModal(ref) {
      if (this.$refs[ref]) {
        this.$refs[ref].closeModal()
      }
    },

    closeEditModal() {
      if (this.$refs.editModal) {
        this.$refs.editModal.closeModal()
      }
      this.$nextTick(() => {
        this.editingCategory = null
      })
    },

    async onCreated() {
      this.pagination.current_page = 1
      await this.fetchList()
    },

    async onUpdated(category) {
      await this.fetchList()
      await this.$nextTick()
      this.closeEditModal()
    },

    async editCategory(category) {
      this.editingCategory = category
      await this.$nextTick()
      if (this.$refs.editModal) {
        this.$refs.editModal.showModal()
      }
    },

    async deleteCategory(category) {
      if (!confirm(`Are you sure you want to delete category "${category.name}"?`)) return

      try {
        this.loading = true
        const result = await form_api_submit({
          url: 'wo/category/delete/' + category.id,
          method: 'DELETE',
          show_success_toast: true,
          show_error_toast: true
        })

        if (result.success) {
          this.$toast('Category deleted successfully!', true)
          this.pagination.current_page = 1
          await this.fetchList()
        }
      } catch (error) {
        console.error('Error deleting category:', error)
        this.$toast('Failed to delete category', false)
      } finally {
        this.loading = false
      }
    },

    viewCategory(category) {
      this.$router.push(`/wo/category/profile/${category.external_id}`)
    },

    initFilters() {
      
    },

    applyFilters(activeFilters) {
      this.currentFilters = { ...activeFilters };
      this.pagination.current_page = 1;
      this.fetchList();
    },

    resetPagination() {
      this.pagination.current_page = 1;
      this.fetchList();
    },

    resetAllFilters() {
      if (this.filterManager) {
        this.filterManager.clearAllFilters();
      }
      this.currentFilters = {};
      this.sortColumn = 'name';
      this.sortOrder = 'asc';
      this.pagination.current_page = 1;
      this.fetchList();
    },

    async fetchList() {
      this.loading = true

      const payload = {
        page: this.pagination.current_page,
        ...this.currentFilters
      }

      const result = await form_api_submit({
        url: 'wo/category/list',
        method: 'GET',
        data: payload,
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        this.categories = result.data.list
        this.pagination = result.data.pagination
      }

      this.loading = false
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return
      this.pagination.current_page = page
      this.fetchList()
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortOrder = 'asc'
      }
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'fas fa-sort text-muted'
      }
      return this.sortOrder === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
    }
  }
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable:hover {
  background-color: #f8f9fa;
}

/* Filters Wrapper */
.filters-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding: 8px 0;
}

.filters-wrapper .filters-bar {
  flex: 1;
}

.filters-wrapper .btn {
  white-space: nowrap;
  font-size: 12px;
  padding: 4px 12px;
}

.badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Table styling */
.table-wrapper {
  overflow-x: auto;
}

:deep(table) {
  width: 100%;
  border-collapse: collapse;
}

:deep(th) {
  background-color: #f8f9fa;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  border-bottom: 1px solid #dee2e6;
}

:deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

:deep(tr:hover) {
  background-color: #f8f9fa;
}

/* Dropdown styling */
.dropdown-menu {
  font-size: 12px;
  min-width: 140px;
}

.dropdown-item {
  font-size: 12px;
  padding: 6px 16px;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

/* Responsive */
@media (max-width: 768px) {
  :deep(th),
  :deep(td) {
    padding: 8px 12px;
  }
  
  .filters-wrapper {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-wrapper .btn {
    align-self: flex-end;
  }
}
</style>